/**
 * useMessages Hook
 *
 * Fetches email messages for the active account with folder/label filtering
 * and pagination support via React Query.
 */

import { useQuery } from '@tanstack/react-query';
import { api, EmailMessage, EmailMessageListOptions } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { errorMessage } from '../types';

interface UseMessagesOptions {
  accountId: string | undefined;
  folderId?: string;
  labelId?: string;
  threadId?: string;
  isRead?: boolean;
  isStarred?: boolean;
  importance?: 'low' | 'normal' | 'high';
  limit?: number;
  offset?: number;
  sort?: string;
}

export function useMessages(options: UseMessagesOptions) {
  const { accountId, ...filters } = options;

  const queryResult = useQuery({
    queryKey: queryKeys.email.messages({ accountId, ...filters }),
    queryFn: async () => {
      if (!accountId) throw new Error('No account selected');
      const params: EmailMessageListOptions = { accountId, ...filters };
      return api.listEmailMessages(params);
    },
    enabled: !!accountId,
  });

  return {
    messages: queryResult.data?.messages ?? ([] as EmailMessage[]),
    total: queryResult.data?.total ?? 0,
    limit: queryResult.data?.limit ?? 50,
    offset: queryResult.data?.offset ?? 0,
    loading: queryResult.isLoading,
    error: errorMessage(queryResult.error),
    refresh: queryResult.refetch,
  };
}
